import { DynamicConfig, StatsigClientEventCallback } from '@statsig/client-core'
import { StatsigClient, StatsigContext } from '@statsig/react-bindings'
import { useMemo } from 'react'
import { LocalOverrideAdapterWrapper } from 'uniswap/src/features/gating/LocalOverrideAdapterWrapper'

export { StatsigClient, StatsigOptions, StatsigUser, StorageProvider, TypedReturn } from '@statsig/react-bindings'

export {
  StatsigProvider,
  Storage,
  useClientAsyncInit,
  useExperiment,
  useFeatureGate,
  useLayer,
  useStatsigUser,
} from '@statsig/react-bindings'

// Export StatsigContext for checking if provider exists
export { StatsigContext }

// Statsig is completely disabled - return simple defaults without any Statsig code execution
export function useStatsigClient() {
  return {
    client: {
      loadingStatus: 'Ready' as const,
      on: (_event: 'values_updated', _handler: StatsigClientEventCallback<'values_updated'>) => {},
      off: (_event: 'values_updated', _handler: StatsigClientEventCallback<'values_updated'>) => {},
    },
  }
}

export function useGateValue(_gateName: string, _options?: { disableExposureLog?: boolean }): boolean {
  // Statsig disabled - always return false (default for gates)
  return false
}

const createEmptyDynamicConfig = (configName: string): DynamicConfig =>
  ({
    value: {},
    name: configName,
    ruleID: '',
    details: { reason: 'STATSIG_DISABLED' as const, time: Date.now() },
    __evaluation: null,
    get: <T>(_key: string, defaultValue: T): T => defaultValue,
    getValue: <T>(_key: string, defaultValue: T): T => defaultValue,
  }) as unknown as DynamicConfig

export function useDynamicConfig(configName: string): DynamicConfig {
  // Statsig disabled - return empty config that always returns default values
  return useMemo(() => createEmptyDynamicConfig(configName), [configName])
}

// Use statsigApiKey from environment variables directly to avoid node dependency errors in cloudflare deploys
// Which happens when importing uniswap/src/config in this file
// A dummy key is used in test env b/c the wallet/mobile tests use this file instead of the statsig.native file
const statsigApiKey =
  process.env.NODE_ENV === 'test'
    ? 'dummy-test-key'
    : process.env.REACT_APP_STATSIG_API_KEY ?? process.env.STATSIG_API_KEY

if (!statsigApiKey) {
  throw new Error('STATSIG_API_KEY is not set')
}

let localOverrideAdapter: LocalOverrideAdapterWrapper

export const getOverrideAdapter = (): LocalOverrideAdapterWrapper => {
  if (!localOverrideAdapter) {
    localOverrideAdapter = new LocalOverrideAdapterWrapper(statsigApiKey)
  }
  return localOverrideAdapter
}
// Statsig disabled - return a minimal mock client that never calls Statsig
export const getStatsigClient = (): StatsigClient => {
  const emptyConfig = createEmptyDynamicConfig('')
  return {
    loadingStatus: 'Ready' as const,
    checkGate: (_gateName: string, _options?: { disableExposureLog?: boolean }) => false,
    getDynamicConfig: (_configName: string) => emptyConfig,
    getExperiment: (_experimentName: string) => ({
      get: <T>(_key: string, defaultValue: T): T => defaultValue,
      getValue: <T>(_key: string, defaultValue: T): T => defaultValue,
    }),
    getLayer: (_layerName: string) => ({
      get: <T>(_key: string, defaultValue: T): T => defaultValue,
      getValue: <T>(_key: string, defaultValue: T): T => defaultValue,
    }),
    on: (_event: 'values_updated', _handler: StatsigClientEventCallback<'values_updated'>) => {},
    off: (_event: 'values_updated', _handler: StatsigClientEventCallback<'values_updated'>) => {},
    updateUserAsync: async (_user: unknown) => {},
    getContext: () => ({ user: {} }),
  } as unknown as StatsigClient
}
